import {observer} from 'mobx-react-lite';
import React, {useCallback, useState} from 'react';
import {ContextAction, useDrop} from 'webapp-lib/Functions';
import {classy} from 'webapp-lib/utils';

import {
  DOCUMENT_DATA_TRANSFER_KEY,
  FOLDER_DATA_TRANSFER_KEY,
} from '_/constants/dragData';

import {Folder} from './Folder';
import {FolderTreeItem} from './FolderTreeItem';

type SubFolderTreeProps = {
  folder: Folder;
  selectedFolderId?: string;
  level: number;
  customToolTipText?: string;
  showIconOnly: boolean;
  extraActionsGenerator?: (folder: Folder) => ContextAction<Folder>[];
  onSelected: (folder: Folder) => void;
  onExpand: (folder: Folder) => Promise<void>;
  onCollapse: (folder: Folder) => void;
  isDraggable?: boolean;
  /**
   * A list of data transfer type keys which the dragged item must
   * contain so that it can be dropped (e.g. 'text/lexian-source-folder-id')
   */
  allowedDataTransferKeys: string[];
  onDragStart?: (event: React.DragEvent, folder: Folder) => void;
  onDragEnd?: (event: React.DragEvent) => void;
  onElementDropped?: (
    parameters: Record<string, string>,
    folder: Folder
  ) => void;
};

const USubFolderTree = ({
  folder,
  selectedFolderId,
  level,
  customToolTipText,
  showIconOnly,
  extraActionsGenerator,
  onSelected,
  onCollapse,
  onExpand,
  isDraggable,
  allowedDataTransferKeys,
  onDragStart,
  onDragEnd,
  onElementDropped,
}: SubFolderTreeProps): React.ReactElement => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = useCallback(
    (event: React.DragEvent) => {
      if (onDragStart) {
        onDragStart?.(event, folder);
        // This is necessary so that the the correct cursor is displayed
        setTimeout(
          () => {
            setIsDragging(true);
            folder.disableDropOnSubfolders();
            folder.parent?.disableDrop();
          },
          20,
          true
        );
      }
    },
    [onDragStart, folder]
  );

  const handleDragEnd = useCallback(
    (event: React.DragEvent) => {
      onDragEnd?.(event);
      setIsDragging(false);
      folder.enableDropOnSubfolders();
      folder.parent?.enableDrop();
    },
    [folder, onDragEnd]
  );

  const {dropCallbacks, isDragging: isDraggingOver} = useDrop({
    dataKeys: allowedDataTransferKeys,
    onDropAccept: (dataList) => {
      const sourceFolderId = dataList[FOLDER_DATA_TRANSFER_KEY];
      if (
        !dataList[DOCUMENT_DATA_TRANSFER_KEY] &&
        (folder.isDropDisabled ||
          folder.id === sourceFolderId ||
          (folder.parent && folder.parent.id === sourceFolderId))
      ) {
        return;
      }
      onElementDropped?.(dataList, folder);
    },
  });

  return (
    <li>
      <FolderTreeItem
        actions={extraActionsGenerator ? extraActionsGenerator(folder) : []}
        className={classy({
          'folder-color-black': folder.folderColor === 'BLACK',
          'folder-color-orange': folder.folderColor === 'ORANGE',
          'folder-color-green': folder.folderColor === 'GREEN',
          'folder-color-grey': folder.folderColor === 'GREY',
        })}
        customToolTipText={customToolTipText}
        draggable={isDraggable && level !== 0}
        folder={folder}
        isDragging={isDragging}
        isDraggingOver={isDraggingOver}
        isSelected={selectedFolderId === folder.id}
        level={level}
        showIconOnly={showIconOnly}
        onCollapse={onCollapse}
        onExpand={onExpand}
        onSelected={onSelected}
        {...dropCallbacks}
        {...(isDraggable
          ? {
              onDragStart: handleDragStart,
              onDragEnd: handleDragEnd,
            }
          : {})}
      />

      <ul>
        {folder.isExpanded
          ? folder.subFolders.map((subFolder) => (
              <SubFolderTree
                key={subFolder.id}
                allowedDataTransferKeys={allowedDataTransferKeys}
                extraActionsGenerator={extraActionsGenerator}
                folder={subFolder}
                level={level + 1}
                selectedFolderId={selectedFolderId}
                showIconOnly={showIconOnly}
                onCollapse={onCollapse}
                onElementDropped={onElementDropped}
                onExpand={onExpand}
                onSelected={onSelected}
                {...(isDraggable
                  ? {
                      isDraggable,
                      onDragEnd,
                      onDragStart,
                    }
                  : {})}
              />
            ))
          : null}
      </ul>
    </li>
  );
};
export const SubFolderTree = observer(USubFolderTree);
